import { Box, Slider, Typography, useMediaQuery, useTheme } from '@mui/material';
import { FormattedMessage, FormattedNumber } from 'react-intl';

import LoadingSpinner from 'components/loading-spinner';
import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { RecommendedMarks } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/constants/daily-consumption-recommended-slider-marks';
import { useInputSettingsState } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/context/input-settings-context';
import { ConsumptionRateOptions } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/types/ofd-settings.type';
import { OutlinedBox } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/utils/styled-components';

const PAGE_NAME_ID = 'orderFeed.ofdMySupplyPlan.inputSettings.dailyConsumptionRateTab';

type Props = {
  setConsumptionRateDays: Dispatch<SetStateAction<number>>;
  consumptionRateOptions?: ConsumptionRateOptions[];
  isLoading: boolean;
};

const DailyConsumptionRecommendedSlider: React.FC<Props> = ({ setConsumptionRateDays, consumptionRateOptions, isLoading }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const {
    getOfdSettingsQuery: { data: ofdSettings },
  } = useInputSettingsState();

  const [selectedDay, setSelectedDay] = useState<number>(ofdSettings?.consumptionRateAdjustmentDays || 7);

  // #region HANDLERS

  const consumptionRateKg = useMemo(() => {
    const selectedConsumptionRateOption = consumptionRateOptions?.find((option) => option.days === selectedDay);

    if (!!selectedConsumptionRateOption) {
      return selectedConsumptionRateOption.kg;
    }
    return 0;
  }, [consumptionRateOptions, selectedDay]);

  const handleSliderChange = (event: Event, newValue: number | number[]) => {
    const newSliderValue = newValue as number;
    setSelectedDay(newSliderValue);
    setConsumptionRateDays(newSliderValue);
  };

  // #endregion

  if (isLoading) return <LoadingSpinner />;

  return (
    <>
      <OutlinedBox>
        <Box display={'flex'} justifyContent={'space-between'} flexDirection={!isMobile ? 'row' : 'column'}>
          <Typography>
            <FormattedMessage id={`${PAGE_NAME_ID}.editRecommended.title`} />
          </Typography>
          <Box display={'flex'} gap={2} mb={10} justifyContent={isMobile ? 'space-between' : 'normal'} mt={isMobile ? 4 : 0}>
            <Box
              p={3}
              borderRadius={2}
              display={'flex'}
              alignItems={'center'}
              gap={2}
              sx={{ background: theme.palette.lightBlue.o25 }}>
              <Typography fontWeight={600}>
                {selectedDay} <FormattedMessage id={`${PAGE_NAME_ID}.days`} />
              </Typography>
              <Typography>
                (
                <FormattedNumber
                  value={consumptionRateKg}
                  unit={'kilogram'}
                  style="unit"
                  minimumFractionDigits={0}
                  maximumFractionDigits={0}
                />
                )
              </Typography>
            </Box>
          </Box>
        </Box>
        <Slider
          aria-label="Always visible"
          defaultValue={7}
          min={3}
          max={30}
          step={1}
          value={selectedDay}
          marks={RecommendedMarks}
          valueLabelDisplay="on"
          onChange={handleSliderChange}
        />
      </OutlinedBox>
    </>
  );
};

export default DailyConsumptionRecommendedSlider;
